import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const HeroSection = ({ player }) => {
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    return age;
  };

  const formatPosition = (position) => {
    if (!position) return 'N/A';
    return position?.split('-')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  };

  return (
    <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/5 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-4 border-primary/20">
              <Image
                src={player?.profilePhoto || 'https://images.unsplash.com/photo-1565547213480-b1eaf931f3cd'}
                alt={`${player?.fullName} professional headshot for club recruitment`}
                className="w-full h-full object-cover"
              />
            </div>
            {player?.fifaVerified && (
              <div className="absolute top-4 right-4 bg-success text-success-foreground px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                <Icon name="ShieldCheck" size={20} />
                <span className="font-semibold">FIFA Verified</span>
              </div>
            )}
            {player?.verifiedStatus && (
              <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                <Icon name="CheckCircle" size={20} />
                <span className="font-semibold">Verified Profile</span>
              </div>
            )}
          </div>
          
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground font-headline mb-2">
                {player?.fullName}
              </h1>
              {player?.knownAs && (
                <p className="text-xl text-muted-foreground mb-4">Known as: {player?.knownAs}</p>
              )}
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="bg-primary/10 text-primary px-4 py-2 rounded-lg font-semibold">
                  {formatPosition(player?.primaryPosition)}
                </div>
                {player?.availableForTransfer && (
                  <div className="bg-success/10 text-success px-4 py-2 rounded-lg font-semibold flex items-center gap-2">
                    <Icon name="TrendingUp" size={18} />
                    Available for Transfer
                  </div>
                )}
                {player?.availableForLoan && (
                  <div className="bg-accent/10 text-accent-foreground px-4 py-2 rounded-lg font-semibold">
                    Available for Loan
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Icon name="Calendar" size={18} />
                  <span className="text-sm">Age</span>
                </div>
                <p className="text-2xl font-bold text-foreground">{calculateAge(player?.dateOfBirth)} years</p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Icon name="Flag" size={18} />
                  <span className="text-sm">Nationality</span>
                </div>
                <p className="text-2xl font-bold text-foreground">{player?.nationalityPrimary}</p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Icon name="Ruler" size={18} />
                  <span className="text-sm">Height</span>
                </div>
                <p className="text-2xl font-bold text-foreground">{player?.height ? `${player?.height} cm` : 'N/A'}</p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Icon name="Activity" size={18} />
                  <span className="text-sm">Preferred Foot</span>
                </div>
                <p className="text-2xl font-bold text-foreground capitalize">{player?.preferredFoot || 'N/A'}</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-primary to-accent p-6 rounded-xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-primary-foreground/80 text-sm mb-1">Current Market Value</p>
                  <p className="text-4xl font-bold text-primary-foreground">
                    {player?.currency || '€'}{player?.currentMarketValue || '0.0'}M
                  </p>
                </div>
                <Icon name="TrendingUp" size={48} className="text-primary-foreground/50" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Current Club</p>
                <p className="font-semibold text-foreground">{player?.currentClub || 'Free Agent'}</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">League</p>
                <p className="font-semibold text-foreground">{player?.currentLeague || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;